import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Typography from "@mui/material/Typography";
import { AppShell } from "./features/shell/AppShell";
import { Login } from "./features/auth/Login";
import { BrowseView } from "./features/browse/BrowseView";
import { CatalogsView } from "./features/catalogs/CatalogsView";
import { SearchView } from "./features/search/SearchView";
import { GalleryView } from "./features/gallery/GalleryView";
import { TrashView } from "./features/trash/TrashView";
import { PrintView } from "./features/print/PrintView";
import { DetailRedirect } from "./features/shell/DetailRedirect";
import { ScanView } from "./features/scan/ScanView";
import { ContainerForm } from "./features/browse/ContainerForm";
import { ItemListView } from "./features/items/ItemListView";
import { ItemDetailView } from "./features/items/ItemDetailView";
import { useAppStore } from "./shared/store/app";
import { useT } from "./shared/i18n";
import { useSSEEvent } from "./shared/sse/useSSEEvent";
import { useDemoMode } from "./features/demo/useDemoMode";
import type { SSEEventPayload } from "@egalregal/shared";

/**
 * A route whose content only exists on the writable app. In demo mode a deep
 * link falls back to the read side instead of rendering a form that cannot
 * submit (ADR-0045).
 */
function ReadOnlyRoute({ children }: { children: ReactNode }) {
  const demoMode = useDemoMode();
  return demoMode ? <Navigate to="/browse" replace /> : <>{children}</>;
}

/**
 * The app frame and the single route map (spec §4). Navigation and search
 * state live in the URL; the shell derives its chrome and back targets from
 * it. `/` and `/home` are redirects only — Home is folded into Browse.
 */
export default function App() {
  const t = useT();
  const auth = useAppStore((s) => s.auth);
  const checkAuth = useAppStore((s) => s.checkAuth);
  const loadContainers = useAppStore((s) => s.loadContainers);
  const loadCatalogContainers = useAppStore((s) => s.loadCatalogContainers);
  const loadCatalogs = useAppStore((s) => s.loadCatalogs);
  const loadTrash = useAppStore((s) => s.loadTrash);
  const onSSESync = useAppStore((s) => s.onSSESync);

  // Debounced SSE handlers — avoid overwriting optimistic store state when
  // the user creates containers faster than the server can echo them back.
  const debounceRef = useRef<{ timer?: ReturnType<typeof setTimeout> }>({});
  const debouncedReload = useCallback(
    (data?: SSEEventPayload) => {
      clearTimeout(debounceRef.current.timer);
      debounceRef.current.timer = setTimeout(() => void onSSESync(data), 500);
    },
    [onSSESync],
  );
  const onSSEMedia = useCallback(
    (data?: SSEEventPayload) => {
      // container-updated is debounced; media uploads refresh immediately.
      // Events carrying entryIds route through the store's skip decision;
      // legacy events (no entryIds) keep the immediate container refetch.
      const entryIds = data?.entryIds;
      if (entryIds && entryIds.length > 0) {
        void onSSESync(data);
        return;
      }
      void loadContainers();
      void loadCatalogContainers();
    },
    [onSSESync, loadContainers, loadCatalogContainers],
  );

  // SSE event subscriptions — react to server-side changes
  useSSEEvent("container-updated", debouncedReload);
  useSSEEvent("media-uploaded", onSSEMedia);

  // Check auth on mount
  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (auth === "authenticated") {
      // Catalogs first — the selected entry scopes the container list (ticket
      // 07). loadCatalogs also refreshes the readable union in the background
      // (ticket 12), so it feeds search/index/appearance search without
      // blocking first paint.
      void loadCatalogs().then(() => void loadContainers());
      void loadTrash();
    }
  }, [auth, loadContainers, loadCatalogs, loadTrash]);

  if (auth === "unknown") {
    return (
      <BrowserRouter>
        <AppShell>
          <Typography variant="body1" color="text.secondary">
            {t("loading")}
          </Typography>
        </AppShell>
      </BrowserRouter>
    );
  }

  if (auth === "anonymous") {
    return (
      <BrowserRouter>
        <AppShell>
          <Login />
        </AppShell>
      </BrowserRouter>
    );
  }

  const pageRoutes = (
    <Routes>
      <Route path="/" element={<Navigate to="/browse" replace />} />
      <Route path="/home" element={<Navigate to="/browse" replace />} />
      <Route path="/browse" element={<BrowseView />} />
      <Route path="/browse/:containerId" element={<BrowseView />} />
      <Route path="/catalogs" element={<CatalogsView />} />
      <Route path="/gallery" element={<GalleryView />} />
      <Route path="/search" element={<SearchView />} />
      <Route path="/trash" element={<TrashView />} />
      <Route path="/print" element={<PrintView />} />
      {/* The demo hides write surfaces; a deep link falls back to the read side. */}
      <Route
        path="/scan"
        element={
          <ReadOnlyRoute>
            <ScanView />
          </ReadOnlyRoute>
        }
      />
      <Route
        path="/add"
        element={
          <ReadOnlyRoute>
            <ContainerForm />
          </ReadOnlyRoute>
        }
      />
      <Route
        path="/add/:initialId"
        element={
          <ReadOnlyRoute>
            <ContainerForm />
          </ReadOnlyRoute>
        }
      />
      <Route
        path="/edit/:editingId"
        element={
          <ReadOnlyRoute>
            <ContainerForm />
          </ReadOnlyRoute>
        }
      />
      {/* Legacy links only redirect; the merged container page is /browse/:id. */}
      <Route path="/detail/:selectedId" element={<DetailRedirect />} />
      <Route path="/items" element={<ItemListView />} />
      <Route path="/items/:itemId" element={<ItemDetailView />} />
    </Routes>
  );

  return (
    <BrowserRouter>
      <AppShell>{pageRoutes}</AppShell>
    </BrowserRouter>
  );
}
